import type { ComponentType } from 'preact'
import { Globe, Mail, Sparkles, CheckCircle, Lock, ChevronRight, ChevronDown, Loader2, ExternalLink } from 'lucide-preact'
import { BRAND_CLASSES } from '@/constants/theme'

type StepStatus = 'completed' | 'active' | 'locked'

interface StepConfig {
  title: string
  description: string
  completedDescription: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: ComponentType<any>
  accent: {
    border: string
    bg: string
    iconBg: string
    iconText: string
    buttonClass: string
  }
}

const STEPS: StepConfig[] = [
  {
    title: 'Landing Page',
    description: 'Generate a landing page with Webster to get started.',
    completedDescription: 'Landing page signed off and ready.',
    icon: Globe,
    accent: {
      border: 'border-teal-200 dark:border-teal-800',
      bg: 'bg-gradient-to-br from-primary-50 to-teal-50 dark:from-primary-900/20 dark:to-teal-900/20',
      iconBg: 'bg-teal-100 dark:bg-teal-900/40',
      iconText: BRAND_CLASSES.ACCENT.TEAL,
      buttonClass: BRAND_CLASSES.BUTTON.PRIMARY,
    },
  },
  {
    title: 'Email Template',
    description: 'Create the email template once your landing page is approved.',
    completedDescription: 'Email template created and versioned.',
    icon: Mail,
    accent: {
      border: 'border-purple-200 dark:border-purple-800',
      bg: 'bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20',
      iconBg: 'bg-purple-100 dark:bg-purple-900/40',
      iconText: BRAND_CLASSES.ACCENT.PURPLE,
      buttonClass: BRAND_CLASSES.BUTTON.PRIMARY,
    },
  },
  {
    title: 'AI Assets',
    description: 'Generate remaining campaign assets with AI assistance.',
    completedDescription: 'AI assets are ready to create.',
    icon: Sparkles,
    accent: {
      border: 'border-amber-200 dark:border-amber-800',
      bg: 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20',
      iconBg: 'bg-amber-100 dark:bg-amber-900/40',
      iconText: BRAND_CLASSES.ACCENT.AMBER,
      buttonClass: BRAND_CLASSES.BUTTON.PRIMARY,
    },
  },
]

interface Props {
  hasLandingPageAsset: boolean
  isLpReadyOrSignedOff: boolean
  hasEmailTemplateAsset: boolean
  isEmailReadyOrSignedOff: boolean
  /** ORB-241: when true, an External-mode LP exists. Step 1 is treated as completed; the bypass affordance hides. */
  lpExternalMode?: boolean
  /** ORB-241: External LP's URL (for the completed-step caption). */
  lpExternalUrl?: string | null
  onCreateLandingPage: () => void
  onCreateEmailTemplate: () => void
  onCreateAIAssets: () => void
  /** ORB-241: optional bypass — user supplies their own URL instead of using the AI builder. */
  onBypassLandingPage?: () => void
  creatingLp: boolean
  creatingEmailTemplate: boolean
  /** ORB-243: spinner state while the bulk-generate-emails request is in flight. */
  creatingAIAssets?: boolean
  /** Number of bulk-generated emails still completing server-side. > 0 keeps the
   *  Create AI Assets button disabled even after the 202 response lands. */
  aiAssetsInFlight?: number
  /** True when at least one AI-generated email asset already exists (i.e. step 3
   *  has been kicked off at least once). The button hides in that case — the
   *  per-asset Webster builder is the affordance from there. */
  hasAiGeneratedAssets?: boolean
}

export function AssetCreationPipeline({
  hasLandingPageAsset,
  isLpReadyOrSignedOff,
  hasEmailTemplateAsset,
  isEmailReadyOrSignedOff,
  lpExternalMode = false,
  lpExternalUrl = null,
  onCreateLandingPage,
  onCreateEmailTemplate,
  onCreateAIAssets,
  onBypassLandingPage,
  creatingLp,
  creatingEmailTemplate,
  creatingAIAssets = false,
  aiAssetsInFlight = 0,
  hasAiGeneratedAssets = false,
}: Props) {
  // Step 1 is "completed" if a Builder-mode LP is signed off OR an External-mode LP is in use (no asset/version flow).
  const lpStepCompleted = isLpReadyOrSignedOff || lpExternalMode
  const statuses: StepStatus[] = [
    lpStepCompleted ? 'completed' : 'active',
    !lpStepCompleted ? 'locked' : isEmailReadyOrSignedOff ? 'completed' : 'active',
    !isEmailReadyOrSignedOff ? 'locked' : hasAiGeneratedAssets ? 'completed' : 'active',
  ]

  const completedCount = statuses.filter(s => s === 'completed').length

  // CTA config per step: label, action, loading, visible
  const ctas: Array<{ label: string; loadingLabel: string; action: () => void; loading: boolean; visible: boolean }> = [
    {
      label: 'Generate Landing Page',
      loadingLabel: 'Setting up...',
      action: onCreateLandingPage,
      loading: creatingLp,
      visible: !hasLandingPageAsset,
    },
    {
      label: 'Create Email Template',
      loadingLabel: 'Setting up...',
      action: onCreateEmailTemplate,
      loading: creatingEmailTemplate,
      visible: lpStepCompleted && !hasEmailTemplateAsset,
    },
    {
      label: 'Create AI Assets',
      // Stays "Generating…" while the request is in flight OR while the bulk fan-out
      // still has emails completing server-side (tracked via SignalR by the parent).
      loadingLabel: 'Generating...',
      action: onCreateAIAssets,
      loading: creatingAIAssets || aiAssetsInFlight > 0,
      // Hide once any AI-generated email asset exists — the user uses each asset's own
      // Webster builder from there. Server is already idempotent (skip-existing) so a
      // re-click would no-op, but hiding the affordance avoids the confusion entirely.
      visible: isEmailReadyOrSignedOff && !hasAiGeneratedAssets,
    },
  ]

  return (
    <div className="mb-6">
      {/* Progress bar */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
          <div
            className="h-full bg-green-500 rounded-full transition-all duration-500"
            style={{ width: `${(completedCount / 3) * 100}%` }}
          />
        </div>
        <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">{completedCount}/3</span>
      </div>

      {/* Pipeline steps */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-stretch gap-0">
        {STEPS.map((step, i) => {
          const status = statuses[i]
          const cta = ctas[i]
          const Icon = step.icon

          return (
            <div key={step.title} className="contents">
              {/* Connector before this card (skip first) */}
              {i > 0 && (
                <>
                  <div className="hidden md:flex items-center justify-center px-2">
                    <ChevronRight className="w-5 h-5 text-slate-300 dark:text-slate-600" />
                  </div>
                  <div className="flex md:hidden items-center justify-center py-2">
                    <ChevronDown className="w-5 h-5 text-slate-300 dark:text-slate-600" />
                  </div>
                </>
              )}

              {/* Step card */}
              <div
                className={`
                  relative rounded-xl border p-5 transition-all
                  ${status === 'completed'
                    ? 'border-green-200 dark:border-green-800/50 bg-green-50/50 dark:bg-green-900/10'
                    : status === 'active'
                      ? `${step.accent.border} ${step.accent.bg}`
                      : 'border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 opacity-60'
                  }
                `}
              >
                <div className="text-xs font-medium text-slate-400 dark:text-slate-500 mb-3">
                  Step {i + 1}
                </div>

                {/* Icon */}
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center mb-3
                    ${status === 'completed'
                      ? 'bg-green-100 dark:bg-green-900/30'
                      : status === 'active'
                        ? step.accent.iconBg
                        : 'bg-slate-100 dark:bg-slate-800'
                    }
                  `}
                >
                  {status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : status === 'locked' ? (
                    <Lock className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                  ) : (
                    <Icon className={`w-5 h-5 ${step.accent.iconText}`} />
                  )}
                </div>

                {/* Title */}
                <h4
                  className={`text-sm font-semibold mb-1 ${
                    status === 'locked'
                      ? 'text-slate-400 dark:text-slate-500'
                      : 'text-slate-900 dark:text-slate-100'
                  }`}
                >
                  {step.title}
                </h4>

                {/* Description */}
                <p
                  className={`text-xs mb-4 ${
                    status === 'locked'
                      ? 'text-slate-400 dark:text-slate-500'
                      : 'text-slate-600 dark:text-slate-400'
                  }`}
                >
                  {i === 0 && lpExternalMode
                    ? 'Using an external URL — manage on the Landing Page tab.'
                    : status === 'completed' ? step.completedDescription : step.description}
                </p>

                {/* External-LP URL caption — ORB-241: surface the bypass URL inline */}
                {i === 0 && lpExternalMode && lpExternalUrl && (
                  <a
                    href={lpExternalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline mb-3 max-w-full truncate"
                    title={lpExternalUrl}
                  >
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{lpExternalUrl}</span>
                  </a>
                )}

                {/* CTA button */}
                {status === 'active' && cta.visible && (
                  <button
                    type="button"
                    onClick={cta.action}
                    disabled={cta.loading}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-60 ${step.accent.buttonClass}`}
                  >
                    {cta.loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                    {cta.loading ? cta.loadingLabel : cta.label}
                  </button>
                )}

                {/* Bypass link — ORB-241: skip the AI builder and supply your own URL.
                    Hidden when an External-mode LP already exists (the step is already completed via that path). */}
                {i === 0 && status === 'active' && cta.visible && onBypassLandingPage && !lpExternalMode && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={onBypassLandingPage}
                      disabled={creatingLp}
                      className="text-xs text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 underline underline-offset-2 disabled:opacity-50"
                    >
                      Or use my own URL →
                    </button>
                  </div>
                )}

                {/* Completed badge */}
                {status === 'completed' && (
                  <div className="absolute top-4 right-4">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
